import {
  forwardRef,
  type ChangeEvent,
  type ComponentPropsWithoutRef,
  type ForwardedRef,
  useId,
  useRef,
  useState,
} from "react";
import { cn } from "../../utils/cn";
import { buildDataId, slugifyDataId } from "../../utils/dataId";
import { DSCard } from "../DSCard";
import { DSIcon } from "../DSIcon";
import { resetHiddenInputValue as clearHiddenInputValue } from "./uploaderUtils";

type DsUploaderProps = {
  "data-id"?: string;
  buttonLabel?: string;
  helperText?: string;
  invalidTypeErrorMessage?: string;
  label: string;
  maxFileSizeInBytes?: number;
  maxSizeErrorMessage?: string;
  onChange?: (file: File | null) => void;
  removeFileLabel?: string;
  value?: File | null;
} & Omit<
  ComponentPropsWithoutRef<"input">,
  "children" | "className" | "defaultValue" | "onChange" | "type" | "value"
> & {
    className?: string;
    defaultValue?: File | null;
    description?: string;
    errorMessage?: string;
  };

const DEFAULT_BUTTON_LABEL = "Inserir arquivo";
const DEFAULT_INVALID_TYPE_ERROR_MESSAGE =
  "Formato de arquivo não suportado. Use o formato permitido.";
const DEFAULT_MAX_SIZE_ERROR_MESSAGE = "O arquivo excede o tamanho máximo permitido.";
const DEFAULT_REMOVE_FILE_LABEL = "Remover arquivo";

function normalizeAcceptList(accept?: string) {
  return accept
    ?.split(",")
    .map(value => value.trim().toLowerCase())
    .filter(Boolean);
}

function fileMatchesAcceptList(file: File, accept?: string) {
  const acceptedValues = normalizeAcceptList(accept);

  if (!acceptedValues?.length) {
    return true;
  }

  const fileName = file.name.toLowerCase();
  const fileType = file.type.toLowerCase();

  return acceptedValues.some(acceptedValue => {
    if (acceptedValue.startsWith(".")) {
      return fileName.endsWith(acceptedValue);
    }

    if (acceptedValue.endsWith("/*")) {
      const [acceptedGroup] = acceptedValue.split("/");
      const [fileGroup] = fileType.split("/");

      return Boolean(acceptedGroup) && acceptedGroup === fileGroup;
    }

    return fileType === acceptedValue;
  });
}

function setInputRefValue(
  ref: ForwardedRef<HTMLInputElement> | undefined,
  value: HTMLInputElement | null
) {
  if (!ref) {
    return;
  }

  if (typeof ref === "function") {
    ref(value);
    return;
  }

  ref.current = value;
}

const DsUploader = forwardRef<HTMLInputElement, DsUploaderProps>(function DsUploader(
  {
    accept,
    buttonLabel = DEFAULT_BUTTON_LABEL,
    className,
    defaultValue = null,
    description,
    disabled = false,
    errorMessage,
    helperText,
    id,
    invalidTypeErrorMessage = DEFAULT_INVALID_TYPE_ERROR_MESSAGE,
    label,
    maxFileSizeInBytes,
    maxSizeErrorMessage = DEFAULT_MAX_SIZE_ERROR_MESSAGE,
    onChange,
    removeFileLabel = DEFAULT_REMOVE_FILE_LABEL,
    required = false,
    value,
    "data-id": dataIdProp,
    ...props
  },
  forwardedRef
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const dataId = dataIdProp ?? buildDataId("file-uploader", slugifyDataId(label));
  const uploaderProps = { ...props, "data-id": dataId };
  const descriptionId = description ? `${inputId}-description` : undefined;
  const helperTextId = helperText ? `${inputId}-helper-text` : undefined;
  const errorMessageId = errorMessage ? `${inputId}-error-message` : undefined;
  const internalErrorMessageId = `${inputId}-internal-error-message`;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uncontrolledFile, setUncontrolledFile] = useState<File | null>(defaultValue);
  const [internalErrorMessage, setInternalErrorMessage] = useState<string | null>(null);

  const isControlled = value !== undefined;
  const selectedFile = isControlled ? value : uncontrolledFile;
  const resolvedErrorMessage = errorMessage ?? internalErrorMessage;
  const resolvedErrorMessageId = errorMessage
    ? errorMessageId
    : internalErrorMessage
      ? internalErrorMessageId
      : undefined;
  const describedBy = [descriptionId, helperTextId, resolvedErrorMessageId].filter(Boolean).join(" ");

  function setInputRef(node: HTMLInputElement | null) {
    inputRef.current = node;
    setInputRefValue(forwardedRef, node);
  }

  function resetHiddenInputValue() {
    clearHiddenInputValue(inputRef.current);
  }

  function clearSelectedFile() {
    resetHiddenInputValue();
    setInternalErrorMessage(null);

    if (!isControlled) {
      setUncontrolledFile(null);
    }

    onChange?.(null);
  }

  function validateFile(file: File) {
    if (!fileMatchesAcceptList(file, accept)) {
      return invalidTypeErrorMessage;
    }

    if (maxFileSizeInBytes && file.size > maxFileSizeInBytes) {
      return maxSizeErrorMessage;
    }

    return null;
  }

  function handleFileSelection(event: ChangeEvent<HTMLInputElement>) {
    const file = event.currentTarget.files?.[0];

    if (!file) {
      return;
    }

    const validationError = validateFile(file);

    if (validationError) {
      setInternalErrorMessage(validationError);
      resetHiddenInputValue();

      if (!isControlled) {
        setUncontrolledFile(null);
      }

      onChange?.(null);
      return;
    }

    setInternalErrorMessage(null);

    if (!isControlled) {
      setUncontrolledFile(file);
    }

    onChange?.(file);
  }

  function openFilePicker() {
    if (disabled) {
      return;
    }

    inputRef.current?.click();
  }

  return (
    <DSCard
      className={cn("flex w-full flex-col gap-4 font-inter", className)}
      data-id={dataId}
      data-slot="uploader-root"
    >
      <div className="flex flex-col gap-2">
        <p
          className="text-[14px] font-semibold leading-[normal] tracking-[0px] text-dark-3"
          data-slot="uploader-label"
        >
          {label}
          {required ? "*" : ""}
        </p>
        {description ? (
          <p
            className="text-[14px] font-normal leading-[normal] tracking-[0px] text-gray-5"
            data-slot="uploader-description"
            id={descriptionId}
          >
            {description}
          </p>
        ) : null}
      </div>

      <div className="flex flex-col gap-3">
        <div className="relative" data-slot="uploader-field-wrapper">
          <button
            aria-label={selectedFile ? `${buttonLabel}: ${selectedFile.name}` : buttonLabel}
            aria-describedby={describedBy || undefined}
            aria-invalid={resolvedErrorMessage ? true : undefined}
            className={cn(
              "relative flex h-10 w-full items-center justify-center overflow-visible rounded-[12px] border transition-[background-color,border-color,opacity] duration-150 ease-out",
              resolvedErrorMessage
                ? "border-feedback-danger bg-body-light-mode-1"
                : "border-transparent bg-dark-light-mode-background hover:opacity-90",
              disabled ? "cursor-not-allowed bg-gray-2 opacity-50" : "cursor-pointer"
            )}
            data-id={buildDataId(dataId, "trigger")}
            data-slot="uploader-trigger"
            disabled={disabled}
            onClick={openFilePicker}
            type="button"
          />

          <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-4">
            {selectedFile ? (
              <div className="flex max-w-full items-center gap-2">
                <DSIcon
                  className="shrink-0 text-gray-5"
                  name="DocumentText"
                  size={20}
                  variant="Linear"
                />
                <span className="pointer-events-auto relative group max-w-[min(100%,320px)]">
                  <span
                    className="block truncate text-[16px] font-semibold leading-[normal] tracking-[0px] text-dark-3"
                    data-slot="uploader-file-name"
                  >
                    {selectedFile.name}
                  </span>
                  <span
                    className="pointer-events-none absolute top-full left-1/2 z-10 mt-1 -translate-x-1/2 whitespace-nowrap rounded bg-brand-primary-3 px-3 py-1 text-[14px] font-normal leading-[normal] tracking-[0px] text-body-light-mode-1 opacity-0 shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-opacity duration-150 ease-out group-hover:opacity-100 group-focus-within:opacity-100"
                    data-slot="uploader-file-name-tooltip"
                  >
                    {selectedFile.name}
                  </span>
                </span>
                <span className="pointer-events-auto relative flex items-center justify-center group">
                  <button
                    aria-label={removeFileLabel}
                    className="cursor-pointer rounded-sm text-gray-5 outline-none transition-opacity duration-150 ease-out hover:opacity-80 focus-visible:opacity-80 disabled:cursor-not-allowed"
                    data-id={buildDataId(dataId, "remove-button")}
                    data-slot="uploader-remove-trigger"
                    disabled={disabled}
                    onClick={event => {
                      event.stopPropagation();
                      clearSelectedFile();
                    }}
                    type="button"
                  >
                    <UploaderCloseIcon />
                  </button>
                  <span
                    className="pointer-events-none absolute top-full left-1/2 z-10 mt-1 -translate-x-1/2 whitespace-nowrap rounded bg-brand-primary-3 px-3 py-1 text-[14px] font-normal leading-[normal] tracking-[0px] text-body-light-mode-1 opacity-0 shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-opacity duration-150 ease-out group-hover:opacity-100 group-focus-within:opacity-100"
                    data-slot="uploader-remove-tooltip"
                  >
                    {removeFileLabel}
                  </span>
                </span>
              </div>
            ) : (
              <span className="flex items-center gap-2 text-[16px] font-semibold leading-[normal] tracking-[0px] text-dark-3">
                <DSIcon
                  className="shrink-0 text-gray-5"
                  name="DocumentUpload"
                  size={20}
                  variant="Linear"
                />
                <span data-slot="uploader-button-label">{buttonLabel}</span>
              </span>
            )}
          </div>

          <input
            {...uploaderProps}
            accept={accept}
            aria-label={label}
            className="sr-only"
            data-id={buildDataId(dataId, "input")}
            data-slot="uploader-input"
            disabled={disabled}
            id={inputId}
            onChange={handleFileSelection}
            ref={setInputRef}
            required={required}
            type="file"
          />
        </div>

        {helperText ? (
          <p
            className="text-[12px] font-normal leading-[normal] tracking-[0px] text-gray-5"
            data-slot="uploader-helper-text"
            id={helperTextId}
          >
            {helperText}
          </p>
        ) : null}

        {resolvedErrorMessage ? (
          <p
            className="text-[12px] font-normal leading-[normal] tracking-[0px] text-feedback-danger"
            data-slot="uploader-error-message"
            id={resolvedErrorMessageId}
          >
            {resolvedErrorMessage}
          </p>
        ) : null}
      </div>
    </DSCard>
  );
});

function UploaderCloseIcon() {
  return (
    <svg
      aria-hidden="true"
      className="size-5"
      fill="none"
      focusable="false"
      viewBox="0 0 20 20"
    >
      <path
        d="M6 6L14 14M14 6L6 14"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  );
}

DsUploader.displayName = "DsUploader";

export { DsUploader };
export type { DsUploaderProps };
